import React from 'react';
import { useSelector } from 'react-redux';
import {
  DateToShortReadableDate,
  FormatAsCurrencyForLocale
} from '@PetSureAustralia/petsure-react-ui-library/distLib/helpers';
import { maskBankInfo } from '../../../utils/maskDetails';
import { SLICES } from '../../../constants/slices';
import './updatedCover.scss';
import {
  ACCOUNT_TYPES,
  PETSURE_UNDERWRITER,
  FREQUENCY_CODE_ANNUALLY,
  PER_FREQUENCY_LABELS
} from '../../../constants/constants';
import { toSentenceCasing } from '../../../utils/formatString';

export const UpdatedCover = () => {
  const { adjustedSummary, renewalSummary } = useSelector((state) => state?.[SLICES.RENEWAL_HUB]);
  const { petDetailsList = [] } = useSelector((state) => state?.[SLICES.PET_DETAILS] || {});
  const currentPetPolicy = petDetailsList.find(({ PolicyNumber }) => PolicyNumber === renewalSummary?.PolicyNumber);

  const bankingInfo = currentPetPolicy?.PolicyDetails?.BankingInfo || {};
  const isDirectDebit = bankingInfo.paymentMethod === ACCOUNT_TYPES.DIRECT_DEBIT;
  const isPetsure = currentPetPolicy?.Underwriter === PETSURE_UNDERWRITER;

  const maskIfNeeded = (value) => (isPetsure ? maskBankInfo(value) : value);

  return (
    <div className="updateCoverPanel">
      <h3>{renewalSummary?.PetName ? `${renewalSummary.PetName}'s updated cover` : 'Updated cover'}</h3>
      <ul>
        <li>
          <em>Policy no</em>
          <strong>{renewalSummary?.FullPolicyNumber}</strong>
        </li>
        <li>
          <em>Level of cover</em>
          <strong>{renewalSummary?.LevelOfCover}</strong>
        </li>
        <li>
          <em>Policy period</em>
          <strong>{`${DateToShortReadableDate(new Date(renewalSummary?.RenewalStartDate))} - ${DateToShortReadableDate(
            new Date(renewalSummary?.RenewalEndDate)
          )}`}</strong>
        </li>
        <li>
          <em>Annual benefit limit</em>
          <strong>
            {FormatAsCurrencyForLocale(renewalSummary?.AnnualBenefitLimit ?? 0, { trimZeroDecimals: true })}
          </strong>
        </li>
        <li>
          <em>Benefit percentage</em>
          <strong>{`${adjustedSummary?.BenefitPercentage || 0}%`}</strong>
        </li>
        <li>
          <em>{toSentenceCasing(renewalSummary?.ExcessType) || 'Excess'}</em>
          <strong>{FormatAsCurrencyForLocale(adjustedSummary?.Excess ?? 0, { trimZeroDecimals: true })}</strong>
        </li>
        <li>
          <em>Routine Care</em>
          <strong>{adjustedSummary?.RoutineCare ? 'Yes' : 'No'}</strong>
        </li>
        <li>
          <em>{renewalSummary?.BoosterCareName || 'Booster Care'}</em>
          <strong>{adjustedSummary?.BoosterCare ? 'Yes' : 'No'}</strong>
        </li>
        <li>
          <em>Annual premium</em>
          <strong>{FormatAsCurrencyForLocale(adjustedSummary?.newPremiumAnnual ?? 0)}</strong>
        </li>
        {renewalSummary?.PaymentFrequency !== FREQUENCY_CODE_ANNUALLY && (
          <li>
            <em>Payment per {PER_FREQUENCY_LABELS[renewalSummary?.PaymentFrequency]?.toLowerCase()}</em>
            <strong>{FormatAsCurrencyForLocale(adjustedSummary?.newPremium ?? 0)}</strong>
          </li>
        )}
        <li>
          <em>Next payment date</em>
          <strong>{DateToShortReadableDate(new Date(renewalSummary?.FirstCollectionDate))}</strong>
        </li>
      </ul>

      <hr />

      <h3>Your premium payment details</h3>
      <ul>
        {isDirectDebit ? (
          <>
            <li>
              <em>Method</em>
              <strong>{bankingInfo.paymentMethod}</strong>
            </li>
            <li>
              <em>Account name</em>
              <strong>{bankingInfo.accountHolderName}</strong>
            </li>
            <li>
              <em>BSB</em>
              <strong>{maskIfNeeded(bankingInfo.bsbNumber)}</strong>
            </li>
            <li>
              <em>Account No.</em>
              <strong>{maskIfNeeded(bankingInfo.accountNumber)}</strong>
            </li>
          </>
        ) : (
          <>
            <li>
              <em>Account type</em>
              <strong>{bankingInfo.paymentMethod}</strong>
            </li>
            <li>
              <em>Card type</em>
              <strong>{bankingInfo.cardType}</strong>
            </li>
            <li>
              <em>Card number</em>
              <strong>{bankingInfo.cardNumber}</strong>
            </li>
          </>
        )}
      </ul>
    </div>
  );
};
